import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import SPMapSection from "./MapSection/SPMapSection";
import SearchCard, { type Option, type SASearchFormFields, SearchTypesSP } from "./SearchCard/SalesPartnerSearchBox";
import SalesPartnersTilesList from "./SalesPartnersTilesList/SalesPartnersTilesList";
import {
  type CenterValue,
  type CurrentLocation,
  type GoogleMarker,
  type PaginationDataType,
  type SalesPartnerMountRequest,
} from "../salesPartnerTypes";
import { markersTransformer } from "../salesPartnerTransformers";
import { redirectToUrl } from "../../utils/url/urlUtils";
import { fetchCurrentLocation } from "../api/fetchCurrentLocation";
import { getSalesPartnerSearch, type UrlParams } from "../api/getSalesPartnerSearch";
import { getBasicSalesPartnerData } from "../api/getBasicSalesPartnerData";
import { type CustomDispatch } from "../../redux/types";
import { type RootState } from "../../redux/store";
import { selectWishlistThunk } from "~/Wishlist/redux/thunks";
import { connect, useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { useIsMinDesktopSize, useIsMinTabletSize } from "../../common/hooks/useMediaQuery";
import { GoogleApiProvider, useIsGoogleApiLoaded } from "../GoogleApiProvider";
import { Col, Row } from "../../common/components/Layout";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import Modal, { ModalSize } from "../../common/components/Modal";
import { SPCompanyDetails } from "./CompanyDetailsCard/SPCompanyDetails";
import GmapsCardRightUnregistered from "./CompanyDetailsCard/SPCompanyDetailsCardRightUnregistered";
import GmapsCardRightRegistered from "./CompanyDetailsCard/SPCompanyDetailsCardRightRegistered";
import { userIsLoggedIn, userIsRegistered } from "../../utils/roles";
import { Button } from "../../common/components/Button/Button";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { hasActiveSelectedSubsidiary, makersWithPreferredSalesPartner } from "../../feature/Affiliate/utilsAccount";
import debounce from "lodash/debounce";
import { useShowAffiliateMainBanner } from "../../feature/Affiliate/utils";
import { RegisterToSaveSnackbar } from "./SalesPartnerSnackbars";
import { trackSalesPartnerFinderSelectDefault } from "../../feature/Analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import classNames from "classnames";
import { SPCompanyDetailsCard } from "./CompanyDetailsCard/SPCompanyDetailsCard";
import { CART_PAGE, CART_STEP_2, REGISTER_PAGE } from "~/utils/url/staticUrls";
import { B2CCartProcessTile } from "~/Cart/pages/CartPage/B2CCart/components/B2CCartProcessTile/B2CCartProcessTile";
import classes from "./SalesPartnerPage.module.less";
import {
  SalesPartnerActions,
  SalesPartnerMethods,
  trackPartnerFinderSearch,
  trackSalesPartnerActions,
} from "~/feature/Analytics/ga/salesPartner";
import {
  transformMarkerListData,
  transformSelectedMarkerData,
} from "~/Cart/pages/CartPage/B2CCart/components/B2CRequestPage/utils";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/feature/Affiliate/redux/selectors";
import { clearSavedSubsidiaryThunk, saveAffiliateThunk } from "~/feature/Affiliate/redux/actions";
import { usePreferredSalesPartner } from "~/SalesPartner/components/UsePreferredSalesPartner";
import { Stacker } from "~/common/components/Stacker/Stacker";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useGuestCheckoutDialog } from "~/Cart/pages/CartPage/B2CCart/components/GuestCheckoutDialog/useGuestCheckoutDialog";
import { Stack } from "@mui/material";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import { Tab } from "~/common/m3/Tabs/Tab";

interface ConfigurationData {
  shareLinearConfigurationData: string[];
  productCompetences: Option[];
  certifiedCompetences: Option[];
  selectedProductCompetences: string[];
}
interface Props {
  data: ConfigurationData | undefined;
}

interface DispatchProps {
  selectWishlist: (wishlistId: string) => void;
}

export const INITIAL_RADIUS = 100;

const RESULTS_ID = "results";

const OFFSET_CONSTANT = 122.88;
const calculateOffset = (zoom: number) => (zoom > 0 ? OFFSET_CONSTANT / 2 ** zoom : OFFSET_CONSTANT);

const SalesPartnerPage = (props: Props & DispatchProps) => {
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const {
    data: { shareLinearConfigurationData, productCompetences, certifiedCompetences, selectedProductCompetences } = {},
  } = props;

  const isCheckout = window.location.pathname.endsWith(CART_STEP_2);

  const PAGE_SIZE = isDesktop || !isCheckout ? 7 : 20;

  const { selectWishlist } = props;
  const { t } = useTranslation();
  const [zoomValue, setZoomValue] = useState<number>(5);
  const [center, setCenter] = useState<CenterValue>({
    lat: 52.520008,
    lng: 13.404954,
  });
  const [markersLoading, setMarkersLoading] = useState<boolean>(true);
  const [allMarkers, setAllMarkers] = useState<GoogleMarker[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<GoogleMarker>();
  const [selectedMarkerMap, setSelectedMarkerMap] = useState<GoogleMarker>();
  const [paginationData, setPaginationData] = useState<PaginationDataType>({
    currentPage: 0,
    numberOfPages: 2,
    pageSize: PAGE_SIZE,
    totalNumberOfResults: 0,
  });
  const [usersCurrentLocation, setUsersCurrentLocation] = useState<CurrentLocation | null>(null);
  const [pageCountryCode, setPageCountryCode] = useState<string>("");
  const [listOfPartnersNames, setListOfPartnersNames] = useState<string[]>([]);
  const [initialized, setInitialized] = useState<boolean>(false);

  const { isLoaded: googleApiLoaded } = useIsGoogleApiLoaded();

  const preferredSalesPartner = usePreferredSalesPartner();
  const savedAffiliate = useSelector(selectSavedAffiliate);
  const visitedAffiliate = useSelector(selectVisitedAffiliate);

  const hasLinearData = !!shareLinearConfigurationData && shareLinearConfigurationData.length > 0;

  const defaultValues: SASearchFormFields = {
    searchType: SearchTypesSP.Address,
    filterByCountry: !preferredSalesPartner,
    filterBySalesPartner: !!preferredSalesPartner,
    radius: preferredSalesPartner ? -1 : INITIAL_RADIUS,
    certifiedCompetence: [],
    productCompetence: isCheckout || hasLinearData ? selectedProductCompetences : [],
    partner: "",
    address: "",
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  const formProps = useForm<SASearchFormFields>({
    defaultValues,
  });

  function sortByTranslatedLabel(products: Option[] | undefined) {
    if (!products) return undefined;

    const getEnglishLabel = (label: string) => {
      return t(label, { lng: "en" });
    };

    // Sort by the english translated label
    const productsWithTranslated = products.slice().sort((a, b) =>
      getEnglishLabel(a.label).localeCompare(getEnglishLabel(b.label)),
    );

    // Map each item to an object that includes the translated label
    return productsWithTranslated.map((item) => ({
      ...item,
      label: t(item.label),
    }));
  }

  const updateSelectedMarker = useCallback((marker: GoogleMarker | undefined) => {
    setSelectedMarker(marker);
    // For some users the addess in SPMap was not updating correctly, even though the partner name was!
    // I tried using key={...} on SPMap to force updating, but it didn't work.
    // I couldn't reproduce issue locally to work on a proper fix, so the fix for now is:
    //
    // Clear selected marker so SPMap is removed from HTML and then quickly recreate it with correct data
    setSelectedMarkerMap(undefined);
    window.setTimeout(() => setSelectedMarkerMap(marker), 50);
  }, []);

  useEffect(() => {
    if (!googleApiLoaded) {
      return;
    }
    getBasicSalesPartnerData().then((res: SalesPartnerMountRequest) => {
      setPageCountryCode(res.code.toLowerCase());
      fetchLocationHandler(res.code.toLowerCase());
      setListOfPartnersNames(res.salesPartnerNames);
    });
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("wl-id")) {
      selectWishlist(String(urlParams.get("wl-id")));
    }
  }, [googleApiLoaded]);

  const fetchLocationHandler = async (countryCode: string) => {
    let geoEnabled = false;

    const hasPreferredSalesPartner = !!preferredSalesPartner;

    let res: CurrentLocation | null = null;
    try {
      res = await fetchCurrentLocation();
    } catch (e: unknown) {
      if (e instanceof GeolocationPositionError && e.code === GeolocationPositionError.PERMISSION_DENIED) {
        // all okay, just no permission
      } else {
        throw e;
      }
    }

    if (res) {
      geoEnabled = true;
      setUsersCurrentLocation(res);
      setCenter({ lat: res.lat, lng: res.lng });
      setMarkersLoading(true);
      formProps.setValue("address", res.fullAddress);
      updateStateOnMount(
        res.fullAddress,
        res.lat,
        res.lng,
        hasPreferredSalesPartner ? "" : res.country.isocode,
        hasPreferredSalesPartner ? -1 : INITIAL_RADIUS,
        preferredSalesPartner?.affiliateId ?? undefined,
      );
    }

    if (!geoEnabled) {
      const country = t(`country.${countryCode}`);
      formProps.setValue("address", country);
      formProps.setValue("radius", -1);
      updateStateWithResponse({
        ...formProps.getValues(),
        address: country,
        partner: "",
        radius: -1,
        filterByCountry: !hasPreferredSalesPartner,
        searchType: SearchTypesSP.Address,
        countryCode: countryCode,
        salesPartnerId: preferredSalesPartner?.affiliateId ?? undefined,
      });
    }
    setInitialized(true);
  };

  //only when accepted google location
  const updateStateOnMount = (
    q: string,
    lat: number,
    lng: number,
    countryCode: string,
    radius: number,
    preferredSalesPartnerId?: string,
  ) => {
    const data = formProps.getValues();
    const objectForUrl: UrlParams = {
      ...data,
      lat,
      lng,
      countryCode,
      radius,
      salesPartnerId: preferredSalesPartnerId,
    };

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getSalesPartnerSearch(objectForUrl, 0, controller.signal).then((response) => {
      const transformedMarkers = markersTransformer(response);
      setAllMarkers(makersWithPreferredSalesPartner(transformedMarkers, preferredSalesPartner?.affiliateId ?? ""));
      setPaginationData({
        ...response.pagination,
        numberOfPages: Math.ceil(response.pagination.totalNumberOfResults / PAGE_SIZE),
        pageSize: PAGE_SIZE,
      });
      setMarkersLoading(false);
      trackSalesPartnerActions(
        SalesPartnerActions.SEARCH,
        SalesPartnerMethods.AUTOMATIC,
        undefined,
        transformMarkerListData(transformedMarkers),
      );
    });
  };

  const updateStateWithResponse = useCallback((inputsData: UrlParams, pageNumber?: number) => {
    setMarkersLoading(true);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    getSalesPartnerSearch(inputsData, pageNumber, controller.signal).then((response) => {
      const markersData = markersTransformer(response);
      const markers = makersWithPreferredSalesPartner(markersData, preferredSalesPartner?.affiliateId ?? "");
      updateSelectedMarker(hasActiveSelectedSubsidiary(markers) && !!preferredSalesPartner ? markers[0] : undefined);
      setAllMarkers(markers);
      setPaginationData({
        ...response.pagination,
        numberOfPages: Math.ceil(response.pagination.totalNumberOfResults / PAGE_SIZE),
        pageSize: PAGE_SIZE,
      });
      setMarkersLoading(false);
      const method =
        inputsData.searchType === SearchTypesSP.Partner ? SalesPartnerMethods.NAME : SalesPartnerMethods.ADDRESS;
      trackPartnerFinderSearch(formProps.getValues(), "Finding Performed", "Click");
      trackSalesPartnerActions(SalesPartnerActions.SEARCH, method, undefined, transformMarkerListData(markers));
    });
  }, []);

  const { val: isDetailsPopupOpen, setTrue: openDetailsPopup, setFalse: closeDetailsPopup } = useBooleanState(false);

  const selectSalesPartner = (marker: GoogleMarker | undefined, canOpenPopup = true) => {
    updateSelectedMarker(marker);

    const pageIndex = marker ? Math.floor(allMarkers.indexOf(marker) / PAGE_SIZE) : 0;
    if (pageIndex >= 0) {
      setPaginationData((oldData) => ({ ...oldData, currentPage: pageIndex }));
    }

    if (marker) {
      const lat = marker.lat + (isDesktop ? calculateOffset(zoomValue) : 0);
      setCenter({ lat: lat, lng: marker.lng });
      window.location.href = "#" + RESULTS_ID;

      if (!isDesktop && canOpenPopup) {
        openDetailsPopup();
      }
    }
    trackSalesPartnerActions(
      SalesPartnerActions.VIEW,
      activeTab === "map" ? SalesPartnerMethods.MAP : SalesPartnerMethods.DETAILS,
      transformSelectedMarkerData(marker),
    );
    trackPartnerFinderSearch(
      formProps.getValues(),
      "Sales Partner Selected",
      "Click",
      transformSelectedMarkerData(marker),
    );
  };

  const updatePaginationCurrentPage = (newPageNumber: number) => {
    setPaginationData({
      ...paginationData,
      currentPage: newPageNumber,
    });
  };

  const showMoreMarkers = () => {
    setPaginationData({
      ...paginationData,
      pageSize: paginationData.pageSize + PAGE_SIZE,
    });
  };

  const listMarkers = allMarkers.slice(
    paginationData.currentPage * paginationData.pageSize,
    (paginationData.currentPage + 1) * paginationData.pageSize,
  );

  const {
    val: showRegisterSnackbar,
    setTrue: openRegisterSnackbar,
    setFalse: closeRegisterSnackbar,
  } = useBooleanState(false);

  useEffect(() => {
    if (!markersLoading && initialized) {
      const countryCode = usersCurrentLocation ? usersCurrentLocation.country.isocode : pageCountryCode;
      getSalesPartnerTiles(countryCode, preferredSalesPartner?.affiliateId);
    }
  }, []);

  const debounceCallback = debounce((pageCountryCode: string, affiliateId: string | undefined) => {
    salesPartnerFinderRequester({
      ...formProps.getValues(),
      countryCode: pageCountryCode,
      preferredSalesPartner: affiliateId,
    });
  }, 1000);
  const getSalesPartnerTiles = useCallback(debounceCallback, []);

  // Used so the use effect does not trigger on initial render
  const isInitialised = useRef(false);

  useEffect(() => {
    if (!isInitialised.current) {
      return;
    }
    if (savedAffiliate && !userIsLoggedIn()) {
      openRegisterSnackbar();
    }
  }, [savedAffiliate]);

  useEffect(() => {
    isInitialised.current = true;
  }, []);

  useEffect(() => {
    if (preferredSalesPartner) {
      const preferredMarker = allMarkers.find((marker) => marker.affiliateId === preferredSalesPartner.affiliateId);
      updateSelectedMarker(preferredMarker);
    }
  }, [allMarkers]);

  const handleConfirmClick = () => {
    closeDetailsPopup();
    if (selectedMarker?.subsidiaryName) {
      handleB2CCheckout(selectedMarker.subsidiaryId);
    }
  };

  // If a preferred sales partner
  useEffect(() => {
    if (preferredSalesPartner) {
      // reset filterCountry and unlimited radius
      formProps.setValue("filterByCountry", false);
      formProps.setValue("radius", -1);
    } else {
      // back to defaults
      formProps.setValue("filterByCountry", true);
      formProps.setValue("radius", INITIAL_RADIUS);
    }
  }, [preferredSalesPartner]);

  const salesPartnerFinderRequester = (
    data: SASearchFormFields & { countryCode?: string; preferredSalesPartner?: string },
  ) => {
    const objectForUrl: UrlParams = {
      ...data,
      countryCode:
        usersCurrentLocation !== null
          ? usersCurrentLocation.country.isocode
          : pageCountryCode || (data.countryCode ?? ""),
      salesPartnerId: data.filterBySalesPartner ? data.preferredSalesPartner ?? "" : "",
    };
    updateStateWithResponse(objectForUrl, 0);
  };

  const submitCb = formProps.handleSubmit((formValues) =>
    salesPartnerFinderRequester({ ...formValues, preferredSalesPartner: preferredSalesPartner?.affiliateId }),
  );

  const setDefaultLocation = (marker: GoogleMarker, isDefault: boolean) => {
    // !userIsPublic() && apicall
    if (isDefault) {
      const address = [marker.address.line1, marker.address.line2, marker.address.town, marker.address.country.name]
        .filter(Boolean)
        .join(" ");
      trackSalesPartnerFinderSelectDefault(marker.address.companyNameDisplay, marker.address.postalCode, address);
      dispatch(
        saveAffiliateThunk({
          name: marker.address.companyNameDisplay,
          affiliateId: marker.affiliateId,
          logo: "",
          subsidiaries: [],
          salesPartnerId: marker.subsidiaryId,
        }),
      );
    } else {
      dispatch(clearSavedSubsidiaryThunk());
    }
  };

  const loggedIn = userIsLoggedIn();
  const registeredUser = userIsRegistered();
  const [activeTab, setActiveTab] = useState("results");

  const results = (
    <SalesPartnersTilesList
      markers={listMarkers}
      selectedMarker={selectedMarker}
      onTileClick={selectSalesPartner}
      paginationData={paginationData}
      markersLoading={markersLoading}
      updatePaginationCurrentPage={updatePaginationCurrentPage}
      showMoreMarkers={showMoreMarkers}
      onToggleDefault={setDefaultLocation}
      isCheckout={isCheckout}
    />
  );

  const map = (
    <SPMapSection
      markers={allMarkers}
      selectedMarker={selectedMarkerMap}
      center={center}
      zoomValue={zoomValue}
      setZoomValue={setZoomValue}
      selectSalesPartner={selectSalesPartner}
      shareLinearConfigurationData={shareLinearConfigurationData}
      openDetailsPopup={openDetailsPopup}
      openRegisterSnackbar={openRegisterSnackbar}
      isCheckout={isCheckout}
      activeTab={activeTab}
    />
  );

  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
  }, [activeTab]);

  const isAffiliate = useShowAffiliateMainBanner();

  const showMapCb = () => {
    setActiveTab("map");
    window.setTimeout(() => redirectToUrl("#" + RESULTS_ID), 0);
    closeDetailsPopup();
  };

  const dispatch = useDispatch();

  const [handleB2CCheckout, guestCheckoutDialog] = useGuestCheckoutDialog();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  return (
    <FormProvider {...formProps}>
      {guestCheckoutDialog}
      {isCheckout && !isAffiliate && <B2CCartProcessTile activeIndex={1} className="mb-4" />}
      <RegisterToSaveSnackbar
        open={showRegisterSnackbar}
        onClose={closeRegisterSnackbar}
        selectedMarker={selectedMarkerMap}
      />
      <form className="scha__sales-partner" onSubmit={submitCb}>
        <h3 className="headline-medium mb-3">{t("partnerFinder.title")}</h3>
        <SearchCard
          usersCurrentLocation={usersCurrentLocation}
          markersLoading={markersLoading}
          pageCountryCode={pageCountryCode}
          listOfPartnersNames={listOfPartnersNames}
          resetForm={() => {
            trackPartnerFinderSearch(formProps.getValues(), "All Information Cleared", "Click");
            formProps.reset({
              ...defaultValues,
              productCompetence: [],
              filterByCountry: false,
              filterBySalesPartner: false,
              radius: -1,
            });
            if (isMobile) {
              submitCb();
            }
          }}
          submitForm={submitCb}
          productCompetencesOptions={sortByTranslatedLabel(productCompetences)}
          certifiedCompetencesOptions={sortByTranslatedLabel(certifiedCompetences)}
          isCheckout={isCheckout || hasLinearData}
        />
      </form>

      <div id={RESULTS_ID}>
        {isDesktop && <DesktopLayout results={results} map={map} />}

        {!isDesktop && <MobileLayout results={results} map={map} activeTab={activeTab} setActiveTab={setActiveTab} />}
      </div>

      {isDetailsPopupOpen && selectedMarker && (
        <Modal
          size={ModalSize.SMALL}
          onClose={closeDetailsPopup}
          modalClassName={classnames(!isTablet && "scha__sp__mobile-details")}
          footerClassName={classnames(!isTablet && classes.filterFooter)}
          footerChildren={
            isTablet ? (
              <div className="d-flex flex-justify-between w-100">
                <Button onClick={closeDetailsPopup} variant="text">
                  {t("bulkUpload.buttons.cancel")}
                </Button>
                <div className={classNames("d-flex", classes.buttons, userIsRegistered() && classes.reverse)}>
                  {isCheckout ? (
                    <Button onClick={handleConfirmClick}>{t("partnerFinder.search.confirm")}</Button>
                  ) : (
                    <Button
                      startIcon={<ShoppingCartOutlinedIcon />}
                      color={userIsRegistered() ? "primary" : "secondary"}
                      href={CART_PAGE}
                    >
                      {t("addToCartPopup.checkoutBtn")}
                    </Button>
                  )}
                  {!userIsRegistered() && (
                    <Button href={REGISTER_PAGE} startIcon={<PersonOutlineOutlinedIcon />}>
                      {t("partnerFinder.register.button")}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <Stacker gap={8} direction="row" className="d-flex flex-justify-between w-100">
                {userIsRegistered() ? (
                  <>
                    <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                      {t("checkoutPage.addressModal.cancel")}
                    </Button>
                    {isCheckout ? (
                      <Button onClick={handleConfirmClick} fullWidth>
                        {t("partnerFinder.search.confirm")}
                      </Button>
                    ) : (
                      <Button startIcon={<ShoppingCartOutlinedIcon />} href={CART_PAGE} fullWidth>
                        {t("addToCartPopup.checkoutBtn")}
                      </Button>
                    )}
                  </>
                ) : (
                  <Stack direction="row" gap={1} className="w-100">
                    <Button onClick={closeDetailsPopup} variant="text" fullWidth>
                      {t("bulkUpload.buttons.cancel")}
                    </Button>
                    <Button onClick={handleConfirmClick} fullWidth>
                      {t("partnerFinder.search.confirm")}
                    </Button>
                  </Stack>
                )}
              </Stacker>
            )
          }
        >
          {!isTablet ? (
            <>
              <SPCompanyDetails
                company={selectedMarker}
                activeTab={activeTab}
                openRegisterSnackbar={openRegisterSnackbar}
                action={
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<Icon type={IconType.Location} />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                }
              />

              <div>
                {!loggedIn && <GmapsCardRightUnregistered company={selectedMarker} />}
                {registeredUser && !isCheckout && (
                  <GmapsCardRightRegistered
                    shareLinearConfigurationData={shareLinearConfigurationData}
                    companyDetails={selectedMarker.address}
                    close={closeDetailsPopup}
                    company={selectedMarker}
                  />
                )}
              </div>
            </>
          ) : (
            <SPCompanyDetailsCard
              selectedMarker={selectedMarker}
              activeTab={activeTab}
              shareLinearConfigurationData={shareLinearConfigurationData}
              openRegisterSnackbar={openRegisterSnackbar}
              isCheckout={isCheckout}
              className="m-0"
              action={
                isDesktop ? undefined : (
                  <div className="scha__sp__mobile-details__show-map">
                    <Button color="secondary" startIcon={<Icon type={IconType.Location} />} onClick={showMapCb}>
                      {t("page.salespartner.showMap")}
                    </Button>
                  </div>
                )
              }
            />
          )}
        </Modal>
      )}
    </FormProvider>
  );
};

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>): DispatchProps => {
  return {
    selectWishlist: (wishlistId) => dispatch(selectWishlistThunk(wishlistId!)),
  };
};

const Cmp = connect(undefined, mapDispatchToProps)(SalesPartnerPage);

export function createSalesPartnerPage(data?: ConfigurationData) {
  return (
    <GoogleApiProvider>
      <Cmp data={data} />
    </GoogleApiProvider>
  );
}

interface LayoutProps {
  results: ReactNode;
  map: ReactNode;
}

interface MobileLayoutProps extends LayoutProps {
  activeTab: string;
  setActiveTab: (a: string) => void;
}

const MobileLayout = ({ results, map, setActiveTab, activeTab }: MobileLayoutProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div className={"scha__sp__tab-container"}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          fullWidthIndicator={false}
          variant={"fullWidth"}
        >
          <Tab
            value={"results"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <DescriptionOutlinedIcon />
                {t("page.salespartner.view.list")}
              </span>
            }
          />
          <Tab
            value={"map"}
            label={
              <span className={"d-flex flex-direction-column flex-align-center"}>
                <FmdGoodOutlinedIcon />
                {t("page.salespartner.view.map")}
              </span>
            }
          />
        </Tabs>
      </div>
      {activeTab === "map" ? map : results}
    </>
  );
};

const DesktopLayout = ({ results, map }: LayoutProps) => {
  return (
    <>
      <Row>
        <Col size={4}>{results}</Col>
        <Col size={8}>{map}</Col>
      </Row>
    </>
  );
};
